// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import GoffyButton from '@/components/GoffyButton';
import { useUser } from '@/app/user-context';
import { PlayingStyleView } from '@/features/playing-style/PlayingStyleView';
import { usePlayingStyle } from '@/features/playing-style/usePlayingStyle';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';

/** "My playing style": how the signed-in player plays, measured from every hole they have scored. */
export default function PlayingStyleScreen() {
  const { user } = useUser();
  const { t } = useI18n();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { playingStyle, loading, error, reload } = usePlayingStyle({ apiBaseUrl, token: user?.token });

  if (!user?.token) {
    return (
      <View style={styles.centered}>
        <Text style={styles.message}>{t('playingStyle.signedOut')}</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centered}>
        <Text style={styles.message}>{t(`playingStyle.${error}`)}</Text>
        <GoffyButton label={t('playingStyle.retry')} onPress={reload} testID="playing-style-retry" />
      </View>
    );
  }

  if (loading && !playingStyle) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="small" color={appColors.accent.primary} />
        <Text style={styles.message}>{t('playingStyle.loading')}</Text>
      </View>
    );
  }

  if (!playingStyle || playingStyle.sampleHoles === 0) {
    return (
      <View style={styles.centered} testID="playing-style-empty">
        <Text style={styles.message}>{t('playingStyle.emptyTitle')}</Text>
        <Text style={styles.messageHint}>{t('playingStyle.emptyHint')}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.screen} contentContainerStyle={styles.content}>
      <PlayingStyleView playingStyle={playingStyle} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: appColors.background.app,
  },
  content: {
    padding: 12,
    width: '100%',
    maxWidth: 720,
    alignSelf: 'center',
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: 24,
  },
  message: {
    fontSize: 14,
    color: appColors.text.secondary,
    textAlign: 'center',
  },
  messageHint: {
    fontSize: 12.5,
    color: appColors.text.muted,
    textAlign: 'center',
    maxWidth: 320,
  },
});
